import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import TrialBanner from "../components/TrialBanner";
import { FUNDER_NAV } from "./FunderTerminal";
import { TrendingUp, ChevronRight, Eye, Star, ShieldCheck, MessageSquare, Users, CheckCircle2 } from "lucide-react";

const STAGE_LABELS = {
  viewed:        { label: "Viewed",        Icon: Eye,            color: "neutral" },
  saved:         { label: "Saved",         Icon: Star,           color: "amber" },
  vdr_opened:    { label: "VDR Opened",    Icon: ShieldCheck,    color: "sky" },
  replied:       { label: "Replied",       Icon: MessageSquare,  color: "violet" },
  in_discussion: { label: "In Discussion", Icon: Users,          color: "teal" },
  closed:        { label: "Closed",        Icon: CheckCircle2,   color: "green" },
};

const HEADER = {
  neutral: "bg-neutral-100 text-neutral-700 border-neutral-200",
  amber:   "bg-amber-50 text-amber-800 border-amber-200",
  sky:     "bg-sky-50 text-sky-800 border-sky-200",
  violet:  "bg-violet-50 text-violet-800 border-violet-200",
  teal:    "bg-[#0A8B85]/10 text-[#0A8B85] border-[#0A8B85]/20",
  green:   "bg-green-50 text-green-800 border-green-200",
};

export default function FunderPipeline() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [expired, setExpired] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/funder/pipeline");
      setData(data); setExpired(false);
    } catch (e) {
      if (e.response?.status === 402) setExpired(true);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const move = async (company_id, stage) => {
    await api.put(`/funder/pipeline/${company_id}`, { stage });
    await load();
  };

  return (
    <AppShell nav={FUNDER_NAV}>
      <TrialBanner />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">Deal pipeline</h1>
          <p className="mt-1 text-sm text-neutral-600">A single Kanban view of every SME flowing through your funnel.</p>
        </div>
        <p className="text-xs text-neutral-500">Auto-stages from views, saves, VDR access & messages. Move cards to In Discussion / Closed manually.</p>
      </div>

      {expired && <p className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">Your trial has ended. Upgrade to access the pipeline.</p>}

      {!data ? <p className="mt-8 text-sm text-neutral-500">Loading…</p> :
        <div className="mt-6 flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
          {data.stages.map(s => {
            const cfg = STAGE_LABELS[s];
            const items = data.groups[s] || [];
            const Icon = cfg.Icon;
            return (
              <div key={s} data-testid={`pipeline-col-${s}`} className="flex w-72 shrink-0 flex-col">
                <div className={"flex items-center gap-2 rounded-t-xl border-b-2 px-3 py-2.5 text-xs font-bold uppercase tracking-wider " + HEADER[cfg.color]}>
                  <Icon className="h-3.5 w-3.5"/> {cfg.label}
                  <span className="ml-auto rounded-full bg-white/70 px-2 text-[10px]">{items.length}</span>
                </div>
                <div className="flex-1 space-y-3 rounded-b-xl bg-neutral-50 p-2 min-h-[140px]">
                  {items.length === 0 && <p className="px-2 py-6 text-center text-xs text-neutral-400">Empty</p>}
                  {items.map(sme => (
                    <div key={sme.company_id} data-testid={`pipeline-card-${sme.company_id}`}
                      className="rounded-lg border border-neutral-200 bg-white p-3 text-left hover:border-[#0A8B85]/40 hover:shadow-sm">
                      <div className="flex items-start justify-between gap-2">
                        <button onClick={()=>nav(`/funder/sme/${sme.company_id}`)} className="flex-1 text-left">
                          <p className="text-sm font-bold text-neutral-900 truncate">{sme.company_name}</p>
                          <p className="text-[11px] text-neutral-500">{sme.country} · {sme.sector}</p>
                        </button>
                        <div className="flex items-center gap-1 text-[#0A8B85]">
                          <TrendingUp className="h-3 w-3"/>
                          <span className="text-sm font-extrabold">{sme.compatibility}</span>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="text-[10px] text-neutral-500">${((sme.ticket_min||0)/1000).toFixed(0)}K–${((sme.ticket_max||0)/1000).toFixed(0)}K</span>
                        <select value={s} onChange={e=>move(sme.company_id, e.target.value)} data-testid={`pipeline-move-${sme.company_id}`}
                          onClick={e=>e.stopPropagation()}
                          className="rounded border border-neutral-200 bg-white px-1.5 py-0.5 text-[10px] outline-none focus:border-[#0A8B85]">
                          {data.stages.map(st => <option key={st} value={st}>{STAGE_LABELS[st].label}</option>)}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>}
    </AppShell>
  );
}
